import {
  getConceptCardPosterIndex,
  getConceptCardImageUrls,
  getConceptDisplayCardKey,
  getCardCopyText,
  copyImageToClipboard,
  copyTextToClipboard,
  downloadConceptZip,
  downloadDesignZip,
  downloadShiftJisText,
  getDownloadTextContent,
  isConceptProjectType,
  isDesignProjectType,
  buildDownloadFilename,
} from '@/shared/helpers/generationHistory';

const generationHistoryCardActionsMixin = {
  data() {
    return {
      collapsedCards: {},
      copiedActionKeys: {},
      copyResetTimers: {},
      downloadLoading: false,
    };
  },
  beforeDestroy() {
    Object.values(this.copyResetTimers).forEach((timerId) => clearTimeout(timerId));
  },
  methods: {
    resolveCardProjectType(card) {
      return (this.detail && this.detail.project_type) || card.project_type;
    },
    resolveDownloadListIndex(card, cardIndex) {
      if (cardIndex != null && cardIndex !== '') {
        return Number(cardIndex) + 1;
      }
      return null;
    },
    isCardCollapsed(index) {
      return !!this.collapsedCards[index];
    },
    toggleCard(index) {
      this.$set(this.collapsedCards, index, !this.collapsedCards[index]);
    },
    getCopyActionKey(card, type) {
      return `${type}-${getConceptDisplayCardKey(card)}`;
    },
    isCopySuccess(card, type) {
      return !!this.copiedActionKeys[this.getCopyActionKey(card, type)];
    },
    markCopySuccess(card, type) {
      const key = this.getCopyActionKey(card, type);
      this.$set(this.copiedActionKeys, key, true);
      if (this.copyResetTimers[key]) {
        clearTimeout(this.copyResetTimers[key]);
      }
      this.copyResetTimers[key] = setTimeout(() => {
        this.$delete(this.copiedActionKeys, key);
        delete this.copyResetTimers[key];
      }, 3000);
    },
    async onCopyText(card) {
      try {
        const projectType = this.resolveCardProjectType(card);
        await copyTextToClipboard(getCardCopyText(card, projectType));
        this.markCopySuccess(card, 'text');
      } catch (e) {
        // silent
      }
    },
    async onCopyImage(card, imageElement) {
      try {
        const projectType = this.resolveCardProjectType(card);
        const isConcept = isConceptProjectType(projectType);
        const imageUrl = isConcept
          ? (getConceptCardImageUrls(card)[0] || '')
          : ((card.output && card.output.url) || '');
        const options = {
          imageElement,
          resultId: card.item_id,
        };
        if (isConcept) {
          options.itemType = card.item_type;
          options.index = getConceptCardPosterIndex(card);
        }
        await copyImageToClipboard(imageUrl, options);
        this.markCopySuccess(card, 'image');
      } catch (e) {
        // silent
      }
    },
    async onCopyPrompt(card) {
      try {
        const prompt = (card.input_meta && card.input_meta.prompt) || '';
        await copyTextToClipboard(prompt);
        this.markCopySuccess(card, 'prompt');
      } catch (e) {
        // silent
      }
    },
    async onDownload(card, cardIndex) {
      const projectType = this.resolveCardProjectType(card);
      if (!projectType) {
        return;
      }
      const listIndex = this.resolveDownloadListIndex(card, cardIndex);
      const needsZip = isDesignProjectType(projectType)
        || (isConceptProjectType(projectType) && getConceptCardImageUrls(card).length);

      if (needsZip) {
        if (this.downloadLoading) {
          return;
        }
        this.downloadLoading = true;
        try {
          if (isDesignProjectType(projectType)) {
            await downloadDesignZip(card, listIndex, this.$t.bind(this));
          } else {
            await downloadConceptZip(card, listIndex, getConceptCardPosterIndex(card));
          }
        } catch (e) {
          // silent
        } finally {
          this.downloadLoading = false;
        }
        return;
      }

      const filename = buildDownloadFilename(projectType, card, listIndex);
      const text = getDownloadTextContent(projectType, card, this.$t.bind(this));
      downloadShiftJisText(filename, text);
    },
  },
};

export default generationHistoryCardActionsMixin;
